import { ApiRef, createApiRef } from '@backstage/core-plugin-api';

/**
 * BackendAuthApi
 *
 * Purpose
 * - Provide a single, centralized way for frontend plugins to obtain an auth token
 *   for backend requests without depending on Okta. In this context it's **not** about
 *   requests to Backstage backend plugins, but rather to other services in
 *   Aura/Neo, like /api/my-service.
 * - In CI/e2e, this API is wired to a Noop implementation that returns an empty
 *   token and never triggers interactive login popups.
 *
 * How to use (in a plugin/component)
 *
 *   import { useApi } from '@backstage/core-plugin-api';
 *   import { backendAuthApiRef } from '@internal/plugin-neo4j-auth-common';
 *
 *   const backendAuth = useApi(backendAuthApiRef);
 *   const token = await backendAuth.getToken();
 *   const headers: token ? { 'MyService-Token': token } : undefined,
 *   await fetch('/api/my-service', { headers, method: 'GET' });
 *
 * Notes
 * - getToken() may return an empty string when no token is available (guest/CI),
 *   so callers should handle missing auth gracefully.
 * - Prefer this API over using oktaAuthApiRef/identityApiRef directly to avoid
 *   leaking authentication details into plugins and to keep CI non-interactive.
 */
export interface BackendAuthApi {
  /** Fetches a token suitable for backend calls. May be an empty string. */
  getToken(): Promise<string>;
}

/**
 * ApiRef for the centralized backend auth API. Provided at the app level.
 */
export const backendAuthApiRef: ApiRef<BackendAuthApi> =
  createApiRef<BackendAuthApi>({
    id: 'internal.neo4j.backend-auth',
  });
